import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Collapsible from '../Collapsible';
import { FilterGroup } from './types/FilterGroup';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { FilterGroupType } from './types/FilterGroupType';
import { FilterGroupOption } from './types/FilterGroupOption';
import FilterGroupElement from './FilterGroupElement';
import { useDispatch } from 'react-redux';
import { queryProductFiltering } from '../../Actions/ProductFiltering.action';

const MAX_FILTERS_BEFORE_SHOW_MORE = 5;

const ProductFiltering = () => {
  const productFiltering = useTypedSelector(
    (state) => state.productFiltering
  );
  const { filters, updatePending } = productFiltering;
  const dispatch = useDispatch();

  const [showAllForFilters, setShowMoreForFilters] = useState<string[]>(
    []
  );
  const [filterSearchResults, setFilterSearchResults] = useState<{
    [filterId: string]: FilterGroupOption[];
  }>({});

  useEffect(() => {
    if (filters?.length! > 0 || updatePending) return;

    dispatch(queryProductFiltering(null, true));
  }, [dispatch]);

  const onShowHideAllClick = (filterId: string) => {
    const listCopy = [...showAllForFilters];
    const index = listCopy.indexOf(filterId);
    if (index == -1) {
      listCopy.push(filterId);
    } else {
      listCopy.splice(index, 1);
    }
    setShowMoreForFilters(listCopy);
  };

  // Returns true if the filter has "show all" selected
  const shouldShowAllFilters = useCallback(
    (filterId: string) => showAllForFilters.indexOf(filterId) != -1,
    [showAllForFilters]
  );

  // Called when the search input was changed for the specified filter
  const onSearch = useCallback(
    (filter: FilterGroup, options: FilterGroupOption[]) => {
      setFilterSearchResults((current) => {
        const filterSearchResultsCopy = { ...current };
        if (!options) {
          if (filterSearchResultsCopy.hasOwnProperty(filter.id)) {
            delete filterSearchResultsCopy[filter.id];
          }
        } else {
          filterSearchResultsCopy[filter.id] = options;
        }
        return filterSearchResultsCopy ?? {};
      });
    },
    []
  );

  // Get options from the specified filter, with regard to the search value for the filter
  const getFilterOptions = useCallback(
    (filter: FilterGroup) =>
      filterSearchResults.hasOwnProperty(filter.id) &&
        filterSearchResults[filter.id] != undefined
        ? filterSearchResults[filter.id] ?? filter.options
        : filter.options,
    [filterSearchResults]
  );

  const getFilterGroupLabel = useCallback(
    (filter: FilterGroup) =>
      `${filter.filterGroupType != FilterGroupType.Range &&
        filter.selectedOptions.length > 0
        ? `(${filter.selectedOptions.length})`
        : ''
        } ${filter.label}`.trim(),
    []
  );

  return (
    <>
      {filters?.length > 0
        ? filters.map((filter) => (
          <Collapsible
            className="product-filtering-collapsible [&_>label]:py-3"
            key={filter.id}
            label={getFilterGroupLabel(filter)}
          >
            <FilterGroupElement
              filter={filter}
              options={getFilterOptions(filter).slice(
                0,
                shouldShowAllFilters(filter.id)
                  ? undefined
                  : MAX_FILTERS_BEFORE_SHOW_MORE
              )}
              showAll={shouldShowAllFilters(filter.id)}
              onSearch={onSearch}
              onShowHideAllClick={onShowHideAllClick}
              showAllButtonVisible={
                getFilterOptions(filter).length >
                MAX_FILTERS_BEFORE_SHOW_MORE
              }
            />
          </Collapsible>
        ))
        : null}
    </>
  );
};

export default ProductFiltering;
